import { motion } from "framer-motion";
import { useState } from "react";
import { ScrollMaskedLines } from "./MaskedText";

const POEM_YT_ID = "ueUXNL-A3Zg";

function PoemVideoEmbed() {
    const [loaded, setLoaded] = useState(false);
    return (
        <div>
            <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
                Watch the reading
            </div>
            <div className="relative aspect-video w-full overflow-hidden bg-neutral-950 border border-neutral-300">
                {loaded ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${POEM_YT_ID}?autoplay=1&rel=0&modestbranding=1`}
                        title="Mistakes Were NOT Made — read by Dr. Tess Lawrie"
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        data-testid="poem-reading-iframe"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setLoaded(true)}
                        data-testid="poem-reading-play"
                        aria-label="Play the poem reading"
                        className="group absolute inset-0 w-full h-full focus:outline-none"
                    >
                        <img
                            src={`https://i.ytimg.com/vi/${POEM_YT_ID}/maxresdefault.jpg`}
                            alt="Video poster"
                            loading="lazy"
                            onError={(e) => {
                                e.currentTarget.src = `https://i.ytimg.com/vi/${POEM_YT_ID}/hqdefault.jpg`;
                            }}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40 pointer-events-none" />
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-white/80 backdrop-blur-[2px] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110">
                                <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/15 transition-colors duration-500" />
                                <svg width="16" height="19" viewBox="0 0 26 30" fill="none" className="ml-1">
                                    <path d="M0 0 L26 15 L0 30 Z" fill="#fff" />
                                </svg>
                            </span>
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
}

const CHAPTERS = [
    {
        n: "01",
        kicker: "August 27, 2021 · Northwell Glen Cove",
        title: ["A cough.", "Eight minutes", "from home."],
        body: [
            "Danielle walked into Northwell Hospital in Glen Cove, New York — eight minutes from where we live — for a cough. She was twenty-eight. She was bright, beautiful, and full of plans.",
            "They told me she could go home after two days. That was the promise. That was the plan I believed.",
        ],
    },
    {
        n: "02",
        kicker: "September 1, 2021 · The lie",
        title: ["They told me", "to go", "take a shower."],
        body: [
            "On September 1st, they told me I could go home and take a shower — that I would be able to come back. That was a lie. They locked me out. They blocked all communication with my daughter. Then they tied her to the bed.",
            "Danielle was scared. Danielle was fighting. She had never spent a night without me. A COVID policy was used to bar her legal guardian — her mother — from the room.",
            "She was left completely alone. Terrified.",
        ],
        image: "/danielle/icu/icu_03_clean.jpg",
        imageAlt: "Danielle in the ICU.",
    },
    {
        n: "03",
        kicker: "Thirty-two days on a ventilator",
        title: ["Sedated.", "Silenced.", "Locked out."],
        body: [
            "Danielle spent thirty-two days on a ventilator, sedated, unable to speak for herself. Her mother, her legal guardian by court decree, was locked out of the building for thirteen of those days.",
            "There are serious discrepancies in Danielle's certified medical records. They are documented, they are before the court, and they will be addressed there.",
        ],
        image: "/danielle/pumps_real.png",
        imageAlt: "The infusion pumps beside Danielle's bed.",
    },
    {
        n: "04",
        kicker: "October 6, 2021",
        title: ["And then,", "on the forty-first", "day."],
        body: [
            "She was gone. Forty-one days of torture. Forty-one days without her mother. Forty-one days stolen.",
            "This is where the counting ended. This is where the accounting began.",
        ],
    },
    {
        n: "05",
        kicker: "The Case · Nassau County Supreme Court",
        title: [
            "The case",
            "is active.",
            "The truth",
            "is coming.",
        ],
        body: [
            "Danielle’s certified hospital record runs thousands of pages. Her mother has read every one of them. What that record contains is now the subject of active litigation in the Supreme Court of the State of New York.",
            "Supreme Court of the State of New York, County of Nassau, Index No. 605957/2024. Three physicians are named as defendants. The case is proceeding.",
            "There are serious discrepancies in Danielle’s certified medical records. They are documented, they are before the court, and they will be addressed there.",
            "Please donate. Please share. Danielle deserves justice.",
            "For Danielle. Always for Danielle.",
            "#JusticeForDanielle  #DanielleAlvarez",
        ],
        image: "/scenes/courtroom.png",
        imageAlt: "An empty formal courtroom, seat of the case.",
        wide: true,
    },
];

export default function ChaptersSection() {
    return (
        <section
            data-testid="chapters-section"
            className="relative w-full bg-[color:var(--paper)] py-24 sm:py-32"
            id="story"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                {/* Section header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 pb-20 sm:pb-28 border-b border-neutral-300"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter II — The Story
                    </div>
                    <div className="col-span-12 sm:col-span-9 mt-4 sm:mt-0">
                        <ScrollMaskedLines
                            as="h2"
                            className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight text-neutral-950"
                            lines={["What happened", "in forty-one days."]}
                        />
                        <p className="mt-8 max-w-2xl font-serif italic text-lg sm:text-xl text-neutral-700 leading-relaxed">
                            The facts as they are recorded. Documented in an
                            active lawsuit in Nassau County Supreme Court, NY.
                        </p>
                    </div>
                </motion.div>

                {/* Chapters */}
                <div className="pt-16 sm:pt-24 space-y-16 sm:space-y-24">
                    {CHAPTERS.map((c, idx) => {
                        const imageLeft = idx % 2 === 1;

                        if (c.wide) {
                            return (
                                <motion.article
                                    key={c.n}
                                    initial={{ opacity: 0, y: 60 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-15%" }}
                                    transition={{
                                        duration: 1.1,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    data-testid={`chapter-${c.n}`}
                                    className="grid grid-cols-12 gap-4 sm:gap-8 items-start"
                                >
                                    {/* Numeral column */}
                                    <div className="col-span-12 sm:col-span-2">
                                        <div className="font-sans tabular text-6xl sm:text-7xl font-light leading-none text-neutral-900">
                                            {c.n}
                                        </div>
                                        <div className="mt-3 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                            {c.kicker}
                                        </div>
                                    </div>

                                    {/* Full-width image */}
                                    <div className="col-span-12 sm:col-span-10 sm:col-start-3 order-1">
                                        <div className="relative w-full aspect-[16/9] overflow-hidden bg-neutral-100 border border-neutral-300">
                                            <img
                                                src={c.image}
                                                alt={c.imageAlt}
                                                className="editorial-img absolute inset-0 w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>

                                    {/* Title + body below, full width */}
                                    <div className="col-span-12 sm:col-span-10 sm:col-start-3 order-2 mt-10 sm:mt-14 grid grid-cols-12 gap-6 sm:gap-8">
                                        <div className="col-span-12 sm:col-span-6 space-y-10">
                                            {/* Poem tribute — Mistakes Were NOT Made */}
                                            <motion.aside
                                                initial={{ opacity: 0, y: 24 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, margin: "-15%" }}
                                                transition={{ duration: 1, delay: 0.2 }}
                                                data-testid="poem-tribute"
                                                className="border-l border-neutral-400 pl-6 sm:pl-8"
                                            >
                                                <div className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                                    An anthem for justice
                                                </div>
                                                <div className="mt-3 font-serif text-2xl sm:text-3xl italic font-light text-neutral-950 leading-[1.1]">
                                                    Mistakes Were{" "}
                                                    <span className="not-italic">NOT</span>{" "}
                                                    Made.
                                                </div>
                                                <div className="mt-2 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                                    By Margaret Anna Alice · Read by Dr. Tess Lawrie
                                                </div>
                                                <p className="mt-6 font-serif italic text-lg sm:text-xl text-neutral-800 leading-snug">
                                                    Rebecca reads this anthem as
                                                    Danielle&rsquo;s. Its closing line
                                                    is ours too — <span className="not-italic font-semibold text-neutral-950">don&rsquo;t let Them get away with it.</span>
                                                </p>
                                                <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 font-sans text-[10px] uppercase tracking-[0.35em]">
                                                    <a
                                                        href="https://margaretannaalice.substack.com/p/mistakes-were-not-made-an-anthem"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid="poem-read-link"
                                                        className="ink-link text-neutral-900 hover:text-neutral-500"
                                                    >
                                                        Read the full poem →
                                                    </a>
                                                </div>

                                                {/* Inline poem video — small, sits right below the writing */}
                                                <div className="mt-8">
                                                    <PoemVideoEmbed />
                                                </div>
                                            </motion.aside>
                                        </div>
                                        <div className="col-span-12 sm:col-span-6 space-y-4">
                                            {c.body.map((p, i) => {
                                                const isHash = p.startsWith("#");
                                                const isForDanielle = p.startsWith("For Danielle");
                                                const isDonate = p.startsWith("Please donate");
                                                const isWeAreNot = p.startsWith("We are not");
                                                return (
                                                    <p
                                                        key={i}
                                                        className={
                                                            isHash
                                                                ? "font-sans text-[11px] uppercase tracking-[0.35em] text-neutral-500 pt-4"
                                                                : isForDanielle
                                                                  ? "font-serif italic text-lg sm:text-xl text-neutral-900"
                                                                  : isDonate
                                                                    ? "font-serif not-italic font-bold text-xl sm:text-2xl leading-snug text-neutral-950"
                                                                    : isWeAreNot
                                                                      ? "font-serif font-semibold text-lg sm:text-xl leading-relaxed text-neutral-950"
                                                                      : "font-serif text-base sm:text-lg lg:text-xl leading-relaxed text-neutral-800"
                                                        }
                                                    >
                                                        {p}
                                                        {isForDanielle && (
                                                            <span className="text-amber-500 not-italic"> ♥</span>
                                                        )}
                                                        {isDonate && (
                                                            <>
                                                                {" "}
                                                                <a
                                                                    href="https://www.givesendgo.com/justicefordanielle?utm_source=share"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    data-testid="chapter-donate-link"
                                                                    className="inline-block mt-3 ml-0 font-sans text-[10px] uppercase tracking-[0.35em] bg-neutral-900 text-neutral-50 px-4 py-2.5 hover:bg-amber-500 hover:text-neutral-900 transition-colors duration-500"
                                                                >
                                                                    Donate on GiveSendGo →
                                                                </a>
                                                            </>
                                                        )}
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.article>
                            );
                        }

                        return (
                            <motion.article
                                key={c.n}
                                initial={{ opacity: 0, y: 60 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{
                                    duration: 1.1,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                data-testid={`chapter-${c.n}`}
                                className="grid grid-cols-12 gap-4 sm:gap-8 items-start"
                            >
                                {/* Numeral */}
                                <div className="col-span-12 sm:col-span-2">
                                    <div className="font-sans tabular text-6xl sm:text-7xl font-light leading-none text-neutral-900">
                                        {c.n}
                                    </div>
                                    <div className="mt-3 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                        {c.kicker}
                                    </div>
                                </div>

                                {/* Text — right side normally, or right of image when imageLeft */}
                                <div
                                    className={`col-span-12 sm:col-span-6 ${
                                        imageLeft ? "sm:col-start-7 order-2 sm:order-2" : "sm:col-start-3 order-2 sm:order-1"
                                    }`}
                                >
                                    <ScrollMaskedLines
                                        as="h3"
                                        className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[0.98] tracking-tight text-neutral-950"
                                        lines={c.title}
                                        stagger={0.09}
                                        duration={0.85}
                                    />
                                    <div className="mt-6 space-y-4">
                                        {c.body.map((p, i) => (
                                            <p
                                                key={i}
                                                className="font-serif text-base sm:text-lg lg:text-xl leading-relaxed text-neutral-800"
                                            >
                                                {p}
                                            </p>
                                        ))}
                                    </div>
                                </div>

                                {/* Image — safely left or right, always after the numeral column */}
                                {c.image && (
                                    <div
                                        className={`col-span-12 sm:col-span-4 ${
                                            imageLeft ? "sm:col-start-3 order-1 sm:order-1" : "sm:col-start-9 order-1 sm:order-2"
                                        }`}
                                    >
                                        <div className="relative w-full aspect-[3/4] overflow-hidden bg-neutral-100 border border-neutral-300">
                                            <img
                                                src={c.image}
                                                alt={c.imageAlt}
                                                className="editorial-img absolute inset-0 w-full h-full object-contain"
                                            />
                                        </div>
                                    </div>
                                )}
                            </motion.article>
                        );
                    })}
                </div>

                {/* Attorney note */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1 }}
                    className="mt-24 sm:mt-32 pt-8 border-t border-neutral-300 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                >
                    Active lawsuit · Nassau County Supreme Court, NY
                </motion.div>
            </div>
        </section>
    );
}
